/** User profiles recognised by the app. Stored literally in 00.Usuarios.Perfil_USR.
 *  `Nova` is the MX-only intermediary role that approves Nova-channel payments
 *  (cobros_nova confirm + pagar_nova confirm). The UsuarioDialog hides it when
 *  the user being edited is not assigned exclusively to Mexico. */
export const PERFILES = ['Admin', 'Administracion', 'Ventas', 'Mantenimiento', 'Nova', 'Marketing', 'Visitante'] as const;
export type Perfil = (typeof PERFILES)[number];

/** Country options exposed in the Usuarios CRUD. `code` matches
 *  `Pais_Codigo_USR` / `Paises_Permitidos_USR`; `label` matches `Pais_USR`. */
export const PAIS_OPTIONS = [
  { label: 'Argentina', code: 'AR' },
  { label: 'Mexico', code: 'MX' },
] as const;
export type PaisLabel = (typeof PAIS_OPTIONS)[number]['label'];
export type PaisCode = (typeof PAIS_OPTIONS)[number]['code'];

export function paisLabelToCode(label: PaisLabel | string | null | undefined): PaisCode | null {
  if (label === 'Argentina') return 'AR';
  if (label === 'Mexico') return 'MX';
  return null;
}
export function paisCodeToLabel(code: PaisCode | string | null | undefined): PaisLabel | null {
  if (code === 'AR') return 'Argentina';
  if (code === 'MX') return 'Mexico';
  return null;
}

/** Perfiles only valid for MX users. Drives the Perfil dropdown filter in
 *  UsuarioDialog: a user whose país set includes AR cannot have one of
 *  these. */
export const MX_ONLY_PERFILES = new Set<Perfil>(['Nova']);

/** Returns true when `perfil` is allowed for a user assigned to every
 *  country in `paises`. Today this is just "Nova requires MX-only", but
 *  the predicate centralises the rule so future country-restricted roles
 *  drop into MX_ONLY_PERFILES (or a sibling set) without rewiring callers. */
export function isPerfilValidForCountries(
  perfil: Perfil,
  paises: readonly PaisLabel[],
): boolean {
  if (!MX_ONLY_PERFILES.has(perfil)) return true;
  // MX-only perfiles require the user to be assigned to Mexico AND
  // nothing else — otherwise the role doesn't exist in the other country.
  return paises.length === 1 && paises[0] === 'Mexico';
}

/**
 * Declarative filter definition for an entity. The entity reads
 * `filterValue` from props and applies it to its own field.
 */
export type EntityFilterSpec = {
  /** Label shown on the filter control (e.g. "Status", "Perfil"). */
  label: string;
  /** Options rendered in the dropdown. `null` value means "all rows". */
  options: { value: string | null; label: string }[];
  /** Default option value (usually `null` = Todos). */
  defaultValue: string | null;
};

const STATUS_FILTER: EntityFilterSpec = {
  label: 'Status',
  defaultValue: 'Activo',
  options: [
    { value: null, label: 'Todos' },
    { value: 'Activo', label: 'Activo' },
    { value: 'Inactivo', label: 'Inactivo' },
  ],
};

/** Entities managed by the Configuración screen (order matches the PowerApps combobox). */
export const CONFIG_ENTITIES = [
  {
    key: 'usuarios',
    label: 'Usuarios',
    singular: 'Usuario',
    filter: {
      label: 'Perfil',
      defaultValue: null,
      options: [
        { value: null, label: 'Todos' },
        ...PERFILES.map((p) => ({ value: p, label: p })),
      ],
    } satisfies EntityFilterSpec,
    secondaryFilter: {
      label: 'Status',
      defaultValue: 'Activo',
      options: [
        { value: null, label: 'Todos' },
        { value: 'Activo', label: 'Activo' },
        { value: 'Inactivo', label: 'Inactivo' },
      ],
    } satisfies EntityFilterSpec,
  },
  { key: 'medios_contacto', label: 'Medios de Contactos', singular: 'Medio de Contacto', filter: STATUS_FILTER },
  { key: 'productos', label: 'Productos', singular: 'Producto', filter: STATUS_FILTER },
  { key: 'proveedores', label: 'Proveedores', singular: 'Proveedor', filter: STATUS_FILTER },
  { key: 'servicios', label: 'Servicios', singular: 'Servicio', filter: STATUS_FILTER },
  // `adminOnly` (read by ConfiguracionPage) hides these from the `administracion`
  // role — only `admin` manages the accounting taxonomy + EF classification.
  { key: 'categorias', label: 'Categorías', singular: 'Categoría', femenino: true, filter: STATUS_FILTER, adminOnly: true },
  { key: 'cuentas_contables', label: 'Cuentas Contables', singular: 'Cuenta Contable', femenino: true, filter: STATUS_FILTER, adminOnly: true },
  // AR-only for now (uncertain whether the Estado Financiero scheme carries
  // to MX). `arOnly` is read by ConfiguracionPage to hide these on MX.
  { key: 'ef_items', label: 'EF Items', singular: 'EF Item', filter: STATUS_FILTER, arOnly: true, adminOnly: true },
  { key: 'ef_subitems', label: 'EF SubItems', singular: 'EF SubItem', filter: STATUS_FILTER, arOnly: true, adminOnly: true },
  // Sin `filter`: no es un ABM de alta/baja sino la carga de la serie de
  // tasas que alimenta la columna Caución del Estado Financiero.
  { key: 'caucion_tasas', label: 'Tasas de Caución', singular: 'Tasa de Caución', femenino: true, arOnly: true, adminOnly: true },
  { key: 'percepciones', label: 'Percepción / Impuesto', singular: 'Percepción / Impuesto', femenino: true, filter: STATUS_FILTER },
  { key: 'medios_pago', label: 'Cajas', singular: 'Caja', femenino: true, filter: STATUS_FILTER },
  // Recargo por cuotas de MercadoPago (% sobre el total factura). AR.
  { key: 'mp_cuotas', label: 'Cuotas MercadoPago', singular: 'Cuota', femenino: true, filter: STATUS_FILTER, arOnly: true },
  // AR-only ARCA emisor identities (nº punto de venta + fiscal print data).
  { key: 'puntos_venta', label: 'Puntos de Venta', singular: 'Punto de Venta', filter: STATUS_FILTER, arOnly: true },
  {
    key: 'parametros',
    label: 'Comisiones / Descuentos',
    singular: 'Parámetro',
    filter: {
      label: 'Tipo de valor',
      defaultValue: null,
      options: [
        { value: null, label: 'Todos' },
        { value: 'Porcentaje', label: 'Porcentaje' },
        { value: 'Numero', label: 'Número' },
      ],
    } satisfies EntityFilterSpec,
  },
  {
    key: 'parametros_sistema',
    label: 'Parámetros',
    singular: 'Parámetro del Sistema',
    filter: STATUS_FILTER,
  },
  {
    key: 'campanias',
    label: 'Campañas',
    singular: 'Campaña',
    femenino: true,
    filter: {
      label: 'Status',
      defaultValue: 'Activo',
      options: [
        { value: null, label: 'Todos' },
        { value: 'Activo', label: 'Activo' },
        { value: 'Inactivo', label: 'Inactivo' },
        { value: 'Finalizado', label: 'Finalizado' },
      ],
    } satisfies EntityFilterSpec,
  },
] as const;

export type ConfigEntityKey = (typeof CONFIG_ENTITIES)[number]['key'];
export const CONFIG_ENTITY_KEYS = CONFIG_ENTITIES.map((e) => e.key);
